export type PriceOption = {
  /** i18n key under pricesSection.labels.* */
  labelKey: string;
  price: string;
  /** Prefix price with translated "from" / "od". */
  priceFrom?: boolean;
};

export type PriceItem = {
  /** Stable English id — also used as i18n key under pricesSection.items.* */
  id: string;
  price?: string;
  priceFrom?: boolean;
  options?: PriceOption[];
  /** Optional note under pricesSection.items.<id>.note */
  hasNote?: boolean;
};

/** Full-complex breed packages (prices as on salon cennik). */
export const FULL_COMPLEX_ITEMS: PriceItem[] = [
  {
    id: 'yorkshireTerrier',
    options: [
      { labelKey: 'under3kg', price: '200zł' },
      { labelKey: 'over3kg', price: '220zł' }
    ]
  },
  {
    id: 'maltese',
    options: [
      { labelKey: 'under3kg', price: '210zł' },
      { labelKey: 'over3kg', price: '220zł' }
    ]
  },
  { id: 'bolognese', price: '220zł' },
  {
    id: 'shihTzu',
    options: [
      { labelKey: 'under3kg', price: '230zł' },
      { labelKey: 'over3kg', price: '240zł' }
    ]
  },
  {
    id: 'havanese',
    options: [
      { labelKey: 'under4kg', price: '220zł' },
      { labelKey: 'over4kg', price: '240zł' }
    ]
  },
  {
    id: 'maltipoo',
    options: [
      { labelKey: 'under30cm', price: '200+zł' },
      { labelKey: 'over30cm', price: '250zł' }
    ]
  },
  { id: 'cavapoo', price: '200–250zł' },
  { id: 'cavachon', price: '250zł' },
  {
    id: 'bichonFrise',
    options: [
      { labelKey: 'under6kg', price: '250zł' },
      { labelKey: 'over6kg', price: '260zł' },
      { labelKey: 'fciShowCut', price: '350zł+' }
    ]
  },
  {
    id: 'poodle',
    options: [
      { labelKey: 'poodleToy', price: '240+zł' },
      { labelKey: 'poodleMini', price: '260zł' },
      { labelKey: 'poodleMedium', price: '350zł' }
    ]
  },
  { id: 'miniatureSpitz', price: '220+zł' },
  { id: 'germanSpitz', price: '250+zł' }
];

export const EXTRA_SERVICE_ITEMS: PriceItem[] = [
  {
    id: 'dematting',
    options: [
      { labelKey: 'perHour', price: '150zł' },
      { labelKey: 'perHourIntense', price: '170zł' }
    ]
  },
  {
    id: 'dogBath',
    options: [
      { labelKey: 'sizeSmall', price: '100–150zł' },
      { labelKey: 'sizeMedium', price: '200zł' },
      { labelKey: 'sizeLarge', price: '220zł', priceFrom: true }
    ]
  },
  { id: 'hygienicTrim', price: '20–40zł' },
  { id: 'poodleFeetOrNails', price: '20–40zł' },
  { id: 'earCleaning', price: '20zł' },
  { id: 'teethBrushing', price: '20zł' },
  { id: 'isbMask', price: '20zł', hasNote: true },
  { id: 'handStrippingHourly', price: '150zł' }
];

const withStripPrices = (
  without: string | null,
  withStrip: string
): PriceOption[] => {
  const options: PriceOption[] = [];
  if (without != null) {
    options.push({ labelKey: 'withoutStripping', price: without });
  }
  options.push({ labelKey: 'withStripping', price: withStrip });
  return options;
};

export const WIREHAIRED_ITEMS: PriceItem[] = [
  {
    id: 'westHighland',
    options: withStripPrices('240zł', '360+zł')
  },
  {
    id: 'norfolkTerrier',
    options: withStripPrices(null, '360+zł')
  },
  {
    id: 'miniatureSchnauzer',
    options: withStripPrices('240zł', '300+zł')
  },
  {
    id: 'standardSchnauzer',
    options: withStripPrices('270zł', '400zł')
  },
  {
    id: 'parsonRussell',
    options: withStripPrices(null, '400zł')
  },
  {
    id: 'jackRussell',
    options: withStripPrices(null, '300+zł')
  },
  {
    id: 'foxTerrier',
    options: withStripPrices('220+zł', '400+zł')
  },
  {
    id: 'irishTerrier',
    options: withStripPrices(null, '350zł')
  },
  {
    id: 'borderTerrier',
    options: withStripPrices('220zł', '400zł')
  },
  {
    id: 'wireHairedDachshund',
    options: withStripPrices(null, '350zł')
  }
];

export const SPANIEL_ITEMS: PriceItem[] = [
  {
    id: 'cavalierKingCharles',
    options: withStripPrices('240zł', '250zł')
  },
  {
    id: 'englishCocker',
    options: withStripPrices('250zł', '300zł')
  },
  {
    id: 'americanCocker',
    options: withStripPrices('250zł', '300zł')
  },
  { id: 'shetlandSheepdog', price: '250zł' }
];

/** English breed ids for large-dog hourly rate (display via i18n). */
export const LARGE_DOG_BREED_IDS = [
  'labrador',
  'goldenRetriever',
  'husky',
  'germanShepherd',
  'borderCollie',
  'oldEnglishSheepdog',
  'berneseMountainDog',
  'portugueseWaterDog',
  'akitaInu',
  'samoyed',
  'standardPoodle',
  'goldendoodle',
  'labradoodle',
  'alaskanMalamute',
  'irishSetter',
  'giantSchnauzer',
  'lagottoRomagnolo',
  'tatraShepherd',
  'australianShepherd',
  'beardedCollieUnder30kg'
] as const;

export type PriceCategoryId =
  | 'fullComplex'
  | 'extras'
  | 'wirehaired'
  | 'spaniels'
  | 'largeDogs';

export const PRICE_CATEGORIES: {
  id: PriceCategoryId;
  tabKey: string;
  titleKey: string;
  descriptionKey?: string;
}[] = [
  {
    id: 'fullComplex',
    tabKey: 'pricesSection.tabs.fullComplex',
    titleKey: 'pricesSection.fullComplex.title',
    descriptionKey: 'pricesSection.fullComplex.description'
  },
  {
    id: 'extras',
    tabKey: 'pricesSection.tabs.extras',
    titleKey: 'pricesSection.extras.title'
  },
  {
    id: 'wirehaired',
    tabKey: 'pricesSection.tabs.wirehaired',
    titleKey: 'pricesSection.wirehaired.title',
    descriptionKey: 'pricesSection.wirehaired.description'
  },
  {
    id: 'spaniels',
    tabKey: 'pricesSection.tabs.spaniels',
    titleKey: 'pricesSection.spaniels.title',
    descriptionKey: 'pricesSection.spaniels.description'
  },
  {
    id: 'largeDogs',
    tabKey: 'pricesSection.tabs.largeDogs',
    titleKey: 'pricesSection.largeDogs.title',
    descriptionKey: 'pricesSection.largeDogs.description'
  }
];

export const itemsForCategory = (id: PriceCategoryId): PriceItem[] => {
  switch (id) {
    case 'fullComplex':
      return FULL_COMPLEX_ITEMS;
    case 'extras':
      return EXTRA_SERVICE_ITEMS;
    case 'wirehaired':
      return WIREHAIRED_ITEMS;
    case 'spaniels':
      return SPANIEL_ITEMS;
    case 'largeDogs':
      return [];
    default:
      return [];
  }
};

export const itemNameKey = (id: string): string =>
  `pricesSection.items.${id}.name`;

export const itemNoteKey = (id: string): string =>
  `pricesSection.items.${id}.note`;

export const labelKeyPath = (labelKey: string): string =>
  `pricesSection.labels.${labelKey}`;

export const largeDogBreedKey = (id: string): string =>
  `pricesSection.largeDogBreeds.${id}`;

/** Compact label shown on collapsed accordion row. */
export const getSummaryPrice = (item: PriceItem): string => {
  if (item.price != null && item.price !== '') {
    return item.price;
  }
  if (item.options == null || item.options.length === 0) {
    return '';
  }
  return item.options[0].price;
};

export const itemHasDetails = (item: PriceItem): boolean =>
  (item.options != null && item.options.length > 0) || item.hasNote === true;
